import _ from "lodash"
import { ColorPalette } from "../config"
import { updateConfig } from "../providers"
import { SessionService } from "./sessionService"
import {
  AuthCredentials,
  ConfigChanges,
  ConfigTypeEnum,
  DocTableColumnConfig,
  EmploymentStatus,
  ProfileNavMenuAccess,
  SystemFeaturesEnum,
  SystemOperationsEnum,
  AssociationId,
  CohortName,
  SelectorConfig,
  SelectorItemsConfig,
  PpeInventoryTransferTableRowItem,
  FieldConfigCompType,
  SystemResourcePrefix,
} from "../types"
import { docsSectionConfig } from "../config"
import { Iam } from "../models"
import { AssociationSettingsRepository } from "../repositories"
import { uploadConfig } from "../providers/endpointDataProvider/config"
import { addUnderScores, processTypes } from "../utils"

const { EMPLOYEE } = EmploymentStatus
const { documents, cohorts, performance, discipline } = SystemFeaturesEnum
const {
  deleteProfileDocuments,
  downloadProfileDocuments,
  viewEmployees,
  viewCandidates,
  viewFaceshots,
  viewProfileDocuments,
  viewPerformance,
  viewDiscipline,
  viewProfileLogs,
} = SystemOperationsEnum

export class ConfigService {
  static async updateConfig(
    selectedAssociation: string,
    configType: ConfigTypeEnum,
    changesToApply: ConfigChanges,
    authCredentials: AuthCredentials,
    dataFlowName?: string,
  ): Promise<{ result: any; updatedConfig: any } | { result: any; updatedConfig?: undefined }> {
    const { username, password } = authCredentials
    const token = await SessionService.prepareAuthTokens(username, password)
    const payload = {
      selectedAssociation: selectedAssociation,
      configType,
      changes: changesToApply,
      dataFlowName: configType === ConfigTypeEnum.DATA_FLOW ? dataFlowName : undefined,
    }
    const response = await updateConfig(payload, token)
    return response
  }

  generateProfileNavMenuConfig(iamEntity: Iam, employmentStatus: EmploymentStatus): ProfileNavMenuAccess {
    const dataAccessOperationType = employmentStatus === EMPLOYEE ? viewEmployees : viewCandidates
    return {
      hasDataManagerAccess: iamEntity.checkAccess(cohorts, dataAccessOperationType),
      hasFaceshotAccess: iamEntity.checkAccess(cohorts, viewFaceshots),
      hasDocPortalAccess: iamEntity.checkAccess(documents, viewProfileDocuments),
      hasPerformanceAccess: iamEntity.checkAccess(performance, viewPerformance),
      hasDisciplineAccess: iamEntity.checkAccess(discipline, viewDiscipline),
      hasLogsAccess: iamEntity.checkAccess(cohorts, viewProfileLogs),
    }
  }

  generateSectionConfig(iamEntity: Iam) {
    return [...docsSectionConfig].filter((sectionConfigItem) => {
      const { sectionName } = sectionConfigItem
      const targetResource = `${SystemResourcePrefix.docViewTab}#${sectionName}`
      return iamEntity.checkAccess(documents, viewProfileDocuments, targetResource)
    })
  }

  generateDocTableColumnConfig(iamEntity: Iam, iconComponentMap: Record<string, JSX.Element>) {
    let columnConfig = [
      { key: "expand", label: "Expand" },
      { key: "collapse", label: "Collapse" },
    ]
    if (iamEntity.checkAccess(documents, downloadProfileDocuments)) {
      columnConfig.push({ key: "download", label: "Download" })
    }
    if (iamEntity.checkAccess(documents, deleteProfileDocuments)) {
      columnConfig.push({ key: "delete", label: "Delete" })
    }
    columnConfig.map((configItem) => {
      return { ...configItem, iconComponent: iconComponentMap[configItem.key] }
    })
    return columnConfig as DocTableColumnConfig
  }

  generateHierarchialAccountPickerConfig(
    associationRepo: AssociationSettingsRepository,
    currentSelections: {
      selectedAssociation: AssociationId
      selectedCohort: CohortName
    },
  ): { selectorConfig: SelectorConfig; selectorItemsConfig: SelectorItemsConfig } {
    const { selectedAssociation, selectedCohort } = currentSelections
    const authedAssociations = associationRepo.getAllAssociationIds()

    let selectorItemsConfig = {} as Record<string, Record<string, any>>
    authedAssociations.forEach((association) => {
      const associationEntity = associationRepo.getAssociationSettingsEntity(association)
      const displayName = associationEntity.getDisplayName()
      const cohorts = associationEntity.getAllCohorts()
      selectorItemsConfig[displayName] = {}
      cohorts.forEach((cohortName) => (selectorItemsConfig[displayName][cohortName] = {}))
    })
    const selectorConfig = [
      {
        key: "association",
        label: "Selected employer",
        value: associationRepo.getAssociationSettingsEntity(selectedAssociation).getDisplayName(),
        selectorItems: [],
      },
      {
        key: "cohort",
        label: "Selected cohort",
        value: selectedCohort,
        selectorItems: [],
      },
    ]
    return { selectorConfig, selectorItemsConfig }
  }

  buildInventoryTransferTableRowConfig(ppeItemOptions: string[]): PpeInventoryTransferTableRowItem {
    const uuid = window.crypto.randomUUID()
    return {
      id: uuid,
      label: {
        key: `item-${uuid}`,
        compType: FieldConfigCompType.SELECTOR,
        value: "",
        label: "",
        selectorItems: ppeItemOptions,
        props: {
          style: { borderBottom: "none", marginTop: 0, marginBottom: 0 },
          fontStyle: { marginTop: 0, border: "none", fontWeight: "500" },
        },
      },
      ppeSize: {
        key: `size-${uuid}`,
        compType: FieldConfigCompType.SELECTOR,
        value: "",
        label: "",
        disabled: true,
        props: {
          style: { borderBottom: "none", marginTop: 0, marginBottom: 0 },
          fontStyle: { marginTop: 0, border: "none", fontWeight: "500" },
        },
      },
      noOfUnits: {
        key: `noOfUnits-${uuid}`,
        compType: FieldConfigCompType.TEXT,
        value: "",
        label: "",
        props: {
          placeholder: "Enter quantity",
          fontStyle: { color: ColorPalette.SECONDARY_TEXT },
        },
      },
      availableToTransfer: "-",
    }
  }

  async uploadConfig(
    association: string,
    configType: ConfigTypeEnum,
    config: Record<string, any>,
    authCredentials: AuthCredentials,
  ) {
    try {
      const { username, password } = authCredentials
      const token = await SessionService.prepareAuthTokens(username, password)
      await uploadConfig(association, configType, config, token)
    } catch (error: any) {
      if (error.message) {
        return error.message.split("\n").map((invaliation: string) => ({ Warning: invaliation }))
      }
      throw error
    }
  }

  extractTargetConfig(
    sourceData: Record<string, any>,
    extractionConfig: { targetKey: string; fallbackKey: string }[],
    fallBackValue = {},
  ) {
    let searchString = ""
    let currentDataLevel = _.cloneDeep(sourceData)
    extractionConfig.forEach((configItem) => {
      const { targetKey, fallbackKey } = configItem
      const keyToAdd = targetKey && targetKey in currentDataLevel ? targetKey : fallbackKey

      if (searchString) {
        searchString = `${searchString}["${keyToAdd}"]`
      } else {
        searchString = `["${keyToAdd}"]`
      }
      currentDataLevel = _.get(sourceData, searchString, fallBackValue)
    })
    return currentDataLevel
  }
}
